import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { EventManager } from '@angular/platform-browser';
import { BehaviorSubject, Observable } from 'rxjs';

import { ApiService } from './api.service';
import { ApiConfig } from '@app/shared/config/api.config';
import { UserDataService } from '@app/user/services/user-data.service';
import { TextAbbrev, TextAbbrevsResponse } from '@app/shared/models/admin.interface';

type Options = {
    element: any;
    keys: string;
};

type KeyEvent = {
    type: 'keydown' | 'keyup';
    key: string;
    event: KeyboardEvent;
};

@Injectable({ providedIn: 'root' })
export class HotkeysService {
    defaults: Partial<Options> = {
        element: this.document,
    };

    public shortCommandsMap = new Map<string, string>()

    constructor(private eventManager: EventManager, @Inject(DOCUMENT) private document: Document, private _apiService: ApiService, private _userService: UserDataService) {
        _userService.isProfileReady.subscribe((isReady: boolean) => {
            if (isReady) {
                this._fetchShortCommands();
            }
        });
    }

    addShortcut(options: Partial<Options>) {
        const merged = { ...this.defaults, ...options };
        const event = `keydown.${merged.keys}`;

        return new Observable((observer) => {
            const handler = (e: any) => {
                e.preventDefault();
                observer.next(e);
            };

            const dispose = this.eventManager.addEventListener(merged.element, event, handler);

            return () => {
                dispose();
            };
        });
    }

    addCodeShortcut(code: string): Observable<KeyboardEvent> {
        return new Observable(observer => {
            const handler = (e: KeyboardEvent) => {
                if (e.code === code && e.altKey) {
                    e.preventDefault();
                    observer.next(e);
                }
            };
            const dispose = this.eventManager.addEventListener(this.document as any, 'keydown', handler);
            return () => dispose();
        });
    }

    trackKeyPressRelease(key: string): Observable<KeyEvent> {
        const targetElement = this.document as any;
        return new Observable((observer) => {
            const keydownHandler = (e: KeyboardEvent) => {
                if (e.key === key || e.code === key) {
                    observer.next({
                        type: 'keydown',
                        key: e.key,
                        event: e
                    });
                }
            };

            const keyupHandler = (e: KeyboardEvent) => {
                if (e.key === key || e.code === key) {
                    observer.next({
                        type: 'keyup',
                        key: e.key,
                        event: e
                    });
                }
            };

            const disposeKeydown = this.eventManager.addEventListener(
                targetElement,
                'keydown',
                keydownHandler
            );

            const disposeKeyup = this.eventManager.addEventListener(
                targetElement,
                'keyup',
                keyupHandler
            );

            return () => {
                disposeKeydown();
                disposeKeyup();
            };
        });
    }

    private _fetchShortCommands() {
        this._apiService.call(ApiConfig.endpoints.getTextAbbrevs, { lang: this._userService.currentUserData$.getValue().lang }).subscribe({
            next: (data: TextAbbrevsResponse) => {
                const keyValueMap = new Map<string, string>();
                data.abbreviations.forEach((abbrev: TextAbbrev) => {
                    keyValueMap.set(abbrev.abbrev, abbrev.text);
                });
                this.shortCommandsMap = keyValueMap;
            },
            error: (error) => {
                console.error('Error fetching short commands:', error);
            }
        });
    }
}
